import React from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import {
  Plus,
  Calendar,
  Link,
  AlertCircle,
  CheckCircle,
  Clock,
  Send,
  RefreshCw,
  BookOpen,
  AlertTriangle,
  Info,
  ArrowRight,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";

const AddDailyUpdate = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      title: "",
      message: "",
      type: "info",
      targetExams: [],
      expiryDate: "",
      actionUrl: "",
      priority: "medium",
      isActive: true,
      updateAt: new Date().toISOString().slice(0, 16),
    },
  });

  const examOptions = [
    "JEE Main",
    "JEE Advanced",
    "NEET",
    "UPSC",
    "CAT",
    "GATE",
    "SSC",
    "Bank PO",
  ];

  const typeOptions = [
    { value: "info", label: "Information", icon: Info, color: "blue" },
    {
      value: "warning",
      label: "Warning",
      icon: AlertTriangle,
      color: "yellow",
    },
    { value: "urgent", label: "Urgent", icon: AlertCircle, color: "red" },
    { value: "success", label: "Success", icon: CheckCircle, color: "green" },
  ];

  const priorityOptions = [
    { value: "low", label: "Low", color: "gray" },
    { value: "medium", label: "Medium", color: "blue" },
    { value: "high", label: "High", color: "orange" },
    { value: "critical", label: "Critical", color: "red" },
  ];

  const onSubmit = async (data) => {
    try {
      console.log("Parsed", new Date(data?.expiryDate).toISOString());
      console.log(data);

      const { data: resData } = await axios.post(
        `${server}/dailyUpdate/new`,
        {
          ...data,
          expiryDate: data?.expiryDate
            ? new Date(data?.expiryDate).toISOString()
            : "",
          updateAt: data?.updateAt
            ? new Date(data?.updateAt).toISOString()
            : "",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(resData?.message);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
      },
    },
  };

  let questionsArr = [
    {
      title: "Quantitative Aptitude Practice",
      message: "Solve 20 questions on Probability and Statistics today",
      type: "practice",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/practice/quantitative-aptitude",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Vocabulary Builder",
      message: "Learn 15 new words for your vocabulary today",
      type: "learning",
      targetExams: ["GRE", "GMAT", "SAT"],
      expiryDate: "2025-11-10",
      actionUrl: "/vocabulary/daily-words",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Reading Comprehension",
      message: "Complete 3 reading comprehension passages",
      type: "practice",
      targetExams: ["CAT", "GRE", "IELTS"],
      expiryDate: "2025-11-12",
      actionUrl: "/practice/reading-comprehension",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Data Interpretation",
      message: "Practice 2 DI sets with charts and graphs",
      type: "practice",
      targetExams: ["CAT", "GMAT", "BANKING"],
      expiryDate: "2025-11-14",
      actionUrl: "/practice/data-interpretation",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Logical Reasoning",
      message: "Solve 15 logical reasoning questions",
      type: "practice",
      targetExams: ["CAT", "GATE", "UPSC"],
      expiryDate: "2025-11-13",
      actionUrl: "/practice/logical-reasoning",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Grammar Practice",
      message: "Complete grammar exercises on tenses and prepositions",
      type: "practice",
      targetExams: ["IELTS", "TOEFL", "GRE"],
      expiryDate: "2025-11-11",
      actionUrl: "/practice/grammar",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Mock Test Reminder",
      message: "Weekly mock test scheduled for this Sunday",
      type: "reminder",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-09",
      actionUrl: "/mock-tests",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Quantitative Formulas",
      message: "Revise important formulas for algebra and geometry",
      type: "revision",
      targetExams: ["CAT", "GMAT", "SAT"],
      expiryDate: "2025-11-16",
      actionUrl: "/revision/quantitative-formulas",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Essay Writing Practice",
      message: "Write an essay on current social issues",
      type: "practice",
      targetExams: ["GRE", "IELTS", "UPSC"],
      expiryDate: "2025-11-15",
      actionUrl: "/practice/essay-writing",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Speed Math Practice",
      message: "Practice quick calculations and mental math",
      type: "practice",
      targetExams: ["CAT", "BANKING", "SSC"],
      expiryDate: "2025-11-12",
      actionUrl: "/practice/speed-math",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Critical Reasoning",
      message: "Solve 10 critical reasoning questions",
      type: "practice",
      targetExams: ["GMAT", "CAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/practice/critical-reasoning",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Sentence Correction",
      message: "Practice 15 sentence correction questions",
      type: "practice",
      targetExams: ["GMAT", "GRE", "SAT"],
      expiryDate: "2025-11-13",
      actionUrl: "/practice/sentence-correction",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Current Affairs Quiz",
      message: "Take today's current affairs quiz",
      type: "quiz",
      targetExams: ["UPSC", "BANKING", "SSC"],
      expiryDate: "2025-11-09",
      actionUrl: "/quizzes/current-affairs",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Vocabulary Quiz",
      message: "Test your vocabulary with today's quiz",
      type: "quiz",
      targetExams: ["GRE", "GMAT", "SAT"],
      expiryDate: "2025-11-10",
      actionUrl: "/quizzes/vocabulary",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Quantitative Quiz",
      message: "Daily quantitative aptitude quiz",
      type: "quiz",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-11",
      actionUrl: "/quizzes/quantitative",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Reading Practice",
      message: "Read and analyze 2 complex passages",
      type: "practice",
      targetExams: ["GRE", "IELTS", "TOEFL"],
      expiryDate: "2025-11-16",
      actionUrl: "/practice/reading",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Data Sufficiency",
      message: "Practice 10 data sufficiency questions",
      type: "practice",
      targetExams: ["GMAT", "CAT"],
      expiryDate: "2025-11-14",
      actionUrl: "/practice/data-sufficiency",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Geometry Practice",
      message: "Solve 15 geometry problems",
      type: "practice",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/practice/geometry",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Algebra Practice",
      message: "Complete algebra problems on equations and inequalities",
      type: "practice",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/practice/algebra",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Number Systems",
      message: "Practice questions on number properties",
      type: "practice",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/practice/number-systems",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Arithmetic Practice",
      message: "Solve percentage, profit-loss, and ratio problems",
      type: "practice",
      targetExams: ["CAT", "BANKING", "SSC"],
      expiryDate: "2025-11-16",
      actionUrl: "/practice/arithmetic",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Verbal Ability",
      message: "Practice para-jumbles and sentence completion",
      type: "practice",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/practice/verbal-ability",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Analytical Writing",
      message: "Practice writing analytical essays",
      type: "practice",
      targetExams: ["GRE", "GMAT"],
      expiryDate: "2025-11-15",
      actionUrl: "/practice/analytical-writing",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Time Management Practice",
      message: "Solve questions with time constraints",
      type: "practice",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/practice/time-management",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Mock Test Analysis",
      message: "Analyze your last mock test performance",
      type: "analysis",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-11",
      actionUrl: "/analysis/mock-tests",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Weak Area Practice",
      message: "Focus on your identified weak areas",
      type: "practice",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/practice/weak-areas",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Strong Area Revision",
      message: "Revise your strong topics for confidence",
      type: "revision",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/revision/strong-areas",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Full Length Test",
      message: "Take a full length practice test",
      type: "test",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/tests/full-length",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Sectional Test",
      message: "Take quantitative aptitude sectional test",
      type: "test",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/tests/sectional/quant",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Verbal Test",
      message: "Take verbal ability sectional test",
      type: "test",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/tests/sectional/verbal",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "DI/LR Test",
      message: "Take data interpretation and logical reasoning test",
      type: "test",
      targetExams: ["CAT"],
      expiryDate: "2025-11-16",
      actionUrl: "/tests/sectional/dilr",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Vocabulary Revision",
      message: "Revise last week's vocabulary words",
      type: "revision",
      targetExams: ["GRE", "GMAT", "SAT"],
      expiryDate: "2025-11-11",
      actionUrl: "/revision/vocabulary",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Formula Revision",
      message: "Revise important mathematical formulas",
      type: "revision",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/revision/formulas",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Grammar Rules Revision",
      message: "Revise key grammar rules and concepts",
      type: "revision",
      targetExams: ["GMAT", "GRE", "IELTS"],
      expiryDate: "2025-11-14",
      actionUrl: "/revision/grammar",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Current Affairs Revision",
      message: "Revise last month's current affairs",
      type: "revision",
      targetExams: ["UPSC", "BANKING", "SSC"],
      expiryDate: "2025-11-15",
      actionUrl: "/revision/current-affairs",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Error Analysis",
      message: "Analyze errors from previous practice sessions",
      type: "analysis",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/analysis/errors",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Performance Tracking",
      message: "Review your progress and performance trends",
      type: "analysis",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/analysis/performance",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Time Management Analysis",
      message: "Analyze your time management in tests",
      type: "analysis",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/analysis/time-management",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Strategy Planning",
      message: "Plan your study strategy for next week",
      type: "planning",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-11",
      actionUrl: "/planning/study-strategy",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Goal Setting",
      message: "Set weekly targets and goals",
      type: "planning",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/planning/goals",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Study Schedule",
      message: "Create detailed study schedule for tomorrow",
      type: "planning",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-09",
      actionUrl: "/planning/schedule",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Resource Planning",
      message: "Identify study resources needed for next topics",
      type: "planning",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/planning/resources",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Motivation Boost",
      message: "Read success stories and motivational content",
      type: "motivation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/motivation/success-stories",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Stress Management",
      message: "Practice relaxation techniques and stress management",
      type: "wellness",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/wellness/stress-management",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Health Check",
      message: "Review your sleep and exercise routine",
      type: "wellness",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/wellness/health-check",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Study Break Activities",
      message: "Plan productive break activities",
      type: "wellness",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/wellness/break-activities",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Peer Learning",
      message: "Discuss concepts with study partners",
      type: "collaboration",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/collaboration/peer-learning",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Doubts Clearing",
      message: "Clear pending doubts with mentors",
      type: "support",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/support/doubts-clearing",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Mentor Session",
      message: "Schedule session with mentor for guidance",
      type: "support",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/support/mentor-session",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Study Group Meeting",
      message: "Attend weekly study group discussion",
      type: "collaboration",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/collaboration/study-group",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Concept Discussion",
      message: "Participate in concept discussion forum",
      type: "collaboration",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/collaboration/forum",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Expert Session",
      message: "Attend expert session on difficult topics",
      type: "learning",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/learning/expert-sessions",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Webinar Alert",
      message: "Join webinar on exam strategies and tips",
      type: "learning",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/learning/webinars",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Video Lessons",
      message: "Watch video lessons on complex topics",
      type: "learning",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/learning/video-lessons",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Study Material Review",
      message: "Review and organize study materials",
      type: "organization",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/organization/materials",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Notes Organization",
      message: "Organize and update your study notes",
      type: "organization",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/organization/notes",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Bookmark Management",
      message: "Organize important bookmarks and resources",
      type: "organization",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/organization/bookmarks",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Progress Report",
      message: "Generate weekly progress report",
      type: "tracking",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/tracking/progress-report",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Milestone Check",
      message: "Check if you achieved this week's milestones",
      type: "tracking",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/tracking/milestones",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Study Time Tracking",
      message: "Review your study time distribution",
      type: "tracking",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/tracking/study-time",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Accuracy Tracking",
      message: "Track your accuracy trends across topics",
      type: "tracking",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/tracking/accuracy",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Speed Tracking",
      message: "Monitor your question solving speed",
      type: "tracking",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/tracking/speed",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Consistency Check",
      message: "Evaluate your study consistency this week",
      type: "evaluation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/evaluation/consistency",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Preparation Level",
      message: "Assess your current preparation level",
      type: "evaluation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/evaluation/preparation-level",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Readiness Score",
      message: "Calculate your exam readiness score",
      type: "evaluation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/evaluation/readiness",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Improvement Areas",
      message: "Identify areas needing immediate improvement",
      type: "evaluation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/evaluation/improvement-areas",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Study Technique Evaluation",
      message: "Evaluate effectiveness of your study techniques",
      type: "evaluation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/evaluation/study-techniques",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Resource Evaluation",
      message: "Evaluate usefulness of study resources",
      type: "evaluation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/evaluation/resources",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Study Environment Check",
      message: "Evaluate and improve your study environment",
      type: "optimization",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/optimization/environment",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Study Routine Optimization",
      message: "Optimize your daily study routine",
      type: "optimization",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/optimization/routine",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Learning Method Optimization",
      message: "Try new learning methods and techniques",
      type: "optimization",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/optimization/learning-methods",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Practice Strategy Optimization",
      message: "Optimize your practice question strategy",
      type: "optimization",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/optimization/practice-strategy",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Revision Strategy Optimization",
      message: "Improve your revision techniques",
      type: "optimization",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/optimization/revision-strategy",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Test Strategy Optimization",
      message: "Refine your test-taking strategies",
      type: "optimization",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/optimization/test-strategy",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Mindset Development",
      message: "Work on developing growth mindset",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/development/mindset",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Confidence Building",
      message: "Practice confidence building exercises",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/development/confidence",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Focus Improvement",
      message: "Practice focus and concentration exercises",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/development/focus",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Memory Techniques",
      message: "Learn and practice memory techniques",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/development/memory",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Problem Solving Skills",
      message: "Develop advanced problem solving approaches",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/development/problem-solving",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Critical Thinking",
      message: "Practice critical thinking exercises",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/development/critical-thinking",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Analytical Skills",
      message: "Develop analytical reasoning abilities",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/development/analytical-skills",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Time Management Skills",
      message: "Practice advanced time management techniques",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/development/time-management",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Decision Making",
      message: "Improve quick decision making skills",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/development/decision-making",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Adaptability Practice",
      message: "Practice adapting to different question types",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/development/adaptability",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Resilience Building",
      message: "Build resilience for challenging situations",
      type: "development",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/development/resilience",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Exam Pattern Familiarity",
      message: "Review exam pattern and question types",
      type: "preparation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/preparation/exam-pattern",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Syllabus Coverage Check",
      message: "Check syllabus coverage progress",
      type: "preparation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/preparation/syllabus",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Important Topics Revision",
      message: "Revise high-weightage topics",
      type: "preparation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/preparation/important-topics",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Exam Day Strategy",
      message: "Plan your exam day strategy and routine",
      type: "preparation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/preparation/exam-day",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Last Minute Tips",
      message: "Review last minute preparation tips",
      type: "preparation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/preparation/last-minute",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Stress Coping Strategies",
      message: "Learn strategies to cope with exam stress",
      type: "preparation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/preparation/stress-coping",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Energy Management",
      message: "Plan your energy levels for exam day",
      type: "preparation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/preparation/energy-management",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Final Checklist",
      message: "Review final preparation checklist",
      type: "preparation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/preparation/checklist",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Success Visualization",
      message: "Practice success visualization techniques",
      type: "motivation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/motivation/visualization",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Positive Affirmations",
      message: "Practice positive self-talk and affirmations",
      type: "motivation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-12",
      actionUrl: "/motivation/affirmations",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Goal Visualization",
      message: "Visualize achieving your target score",
      type: "motivation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-14",
      actionUrl: "/motivation/goal-visualization",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Progress Celebration",
      message: "Celebrate your progress and achievements",
      type: "motivation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-15",
      actionUrl: "/motivation/celebration",
      priority: "low",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Inspiration Reading",
      message: "Read inspirational stories and quotes",
      type: "motivation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-13",
      actionUrl: "/motivation/inspiration",
      priority: "medium",
      isActive: true,
      updateAt: "2025-11-08",
    },
    {
      title: "Mindset Reset",
      message: "Reset your mindset for peak performance",
      type: "motivation",
      targetExams: ["CAT", "GMAT", "GRE"],
      expiryDate: "2025-11-16",
      actionUrl: "/motivation/mindset-reset",
      priority: "high",
      isActive: true,
      updateAt: "2025-11-08",
    },
  ];

  const handleBulkUpload = async () => {
    for (let i = 0; i < questionsArr.length; i++) {
      const { data } = await axios.post(
        `${server}/dailyUpdate/new`,
        {
          ...questionsArr[i],
          expiryDate: new Date(questionsArr[i]?.expiryDate).toISOString(),
          updateAt: new Date(questionsArr[i]?.updateAt).toISOString(),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(i);
    }

    toast.success("Completed");
  };

  return (
    <motion.div
      className="max-w-2xl mx-auto p-6 space-y-6 bg-gray-900 min-h-screen"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* <button onClick={handleBulkUpload}>Click me</button> */}
      {/* Header */}
      <motion.div className="text-center space-y-2" variants={itemVariants}>
        <div className="flex items-center justify-center gap-3">
          <motion.div
            variants={iconVariants}
            className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl shadow-lg shadow-blue-500/20"
          >
            <Plus className="w-6 h-6 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold bg-gradient-to-br from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Add Daily Update
          </h1>
        </div>
        <p className="text-gray-400 text-lg">
          Create a new announcement for students
        </p>
      </motion.div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Title Field */}
        <motion.div variants={itemVariants}>
          <label
            htmlFor="title"
            className="block text-sm font-semibold text-gray-300 mb-2"
          >
            Title *
          </label>
          <div className="relative">
            <input
              id="title"
              type="text"
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 bg-gray-800 text-white placeholder-gray-500 ${
                errors.title
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500/50"
                  : "border-gray-700 focus:border-blue-500 focus:ring-blue-500/50"
              }`}
              placeholder="Enter update title..."
              {...register("title", {
                required: "Title is required",
                minLength: {
                  value: 3,
                  message: "Title must be at least 3 characters",
                },
                maxLength: {
                  value: 100,
                  message: "Title must not exceed 100 characters",
                },
              })}
            />
            {errors.title && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <AlertCircle className="w-5 h-5 text-red-400" />
              </motion.div>
            )}
          </div>
          {errors.title && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-2 text-sm text-red-400 flex items-center gap-1"
            >
              <AlertCircle className="w-4 h-4" />
              {errors.title.message}
            </motion.p>
          )}
        </motion.div>

        {/* Message Field */}
        <motion.div variants={itemVariants}>
          <label
            htmlFor="message"
            className="block text-sm font-semibold text-gray-300 mb-2"
          >
            Message *
          </label>
          <div className="relative">
            <textarea
              id="message"
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 resize-none bg-gray-800 text-white placeholder-gray-500 ${
                errors.message
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500/50"
                  : "border-gray-700 focus:border-blue-500 focus:ring-blue-500/50"
              }`}
              rows="4"
              placeholder="Enter your message here..."
              {...register("message", {
                required: "Message is required",
                minLength: {
                  value: 10,
                  message: "Message must be at least 10 characters",
                },
                maxLength: {
                  value: 1000,
                  message: "Message must not exceed 1000 characters",
                },
              })}
            />
            {errors.message && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute right-3 top-3"
              >
                <AlertCircle className="w-5 h-5 text-red-400" />
              </motion.div>
            )}
          </div>
          {errors.message && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-2 text-sm text-red-400 flex items-center gap-1"
            >
              <AlertCircle className="w-4 h-4" />
              {errors.message.message}
            </motion.p>
          )}
        </motion.div>

        {/* Type and Priority Row */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={itemVariants}
        >
          {/* Type Field */}
          <div>
            <label
              htmlFor="type"
              className="block text-sm font-semibold text-gray-300 mb-2"
            >
              Type *
            </label>
            <div className="relative">
              <select
                id="type"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 appearance-none bg-gray-800 text-white transition-all duration-200"
                {...register("type", { required: true })}
              >
                {typeOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <option
                      key={option.value}
                      value={option.value}
                      className="bg-gray-800"
                    >
                      {option.label}
                    </option>
                  );
                })}
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ArrowRight className="w-4 h-4 text-gray-400 rotate-90" />
              </div>
            </div>
          </div>

          {/* Priority Field */}
          <div>
            <label
              htmlFor="priority"
              className="block text-sm font-semibold text-gray-300 mb-2"
            >
              Priority *
            </label>
            <div className="relative">
              <select
                id="priority"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 appearance-none bg-gray-800 text-white transition-all duration-200"
                {...register("priority", { required: true })}
              >
                {priorityOptions.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="bg-gray-800"
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ArrowRight className="w-4 h-4 text-gray-400 rotate-90" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Target Exams Field */}
        <motion.div variants={itemVariants}>
          <label className="block text-sm font-semibold text-gray-300 mb-3">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Target Exams
            </div>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {examOptions.map((exam) => (
              <motion.label
                key={exam}
                className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  watch("targetExams")?.includes(exam)
                    ? "border-blue-500 bg-blue-500/20 text-blue-300"
                    : "border-gray-700 bg-gray-800 hover:border-gray-600 text-gray-300"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <input
                  type="checkbox"
                  value={exam}
                  className="hidden"
                  {...register("targetExams")}
                />
                <div
                  className={`w-4 h-4 border-2 rounded transition-all duration-200 ${
                    watch("targetExams")?.includes(exam)
                      ? "border-blue-400 bg-blue-400"
                      : "border-gray-500"
                  }`}
                >
                  {watch("targetExams")?.includes(exam) && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-full h-full flex items-center justify-center"
                    >
                      <CheckCircle className="w-3 h-3 text-white" />
                    </motion.div>
                  )}
                </div>
                <span className="text-sm font-medium">{exam}</span>
              </motion.label>
            ))}
          </div>
        </motion.div>

        {/* Dates Row */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={itemVariants}
        >
          {/* Expiry Date */}
          <div>
            <label
              htmlFor="expiryDate"
              className="block text-sm font-semibold text-gray-300 mb-2"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Expiry Date *
              </div>
            </label>
            <div className="relative">
              <input
                id="expiryDate"
                type="date"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 bg-gray-800 text-white ${
                  errors.expiryDate
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500/50"
                    : "border-gray-700 focus:border-blue-500 focus:ring-blue-500/50"
                }`}
                {...register("expiryDate", {
                  required: "Expiry date is required",
                  validate: (value) => {
                    const selectedDate = new Date(value);
                    const now = new Date();
                    return (
                      selectedDate > now || "Expiry date must be in the future"
                    );
                  },
                })}
              />
            </div>
            {errors.expiryDate && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-2 text-sm text-red-400 flex items-center gap-1"
              >
                <AlertCircle className="w-4 h-4" />
                {errors.expiryDate.message}
              </motion.p>
            )}
          </div>

          {/* Update Date */}
          <div>
            <label
              htmlFor="updateAt"
              className="block text-sm font-semibold text-gray-300 mb-2"
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Update Date *
              </div>
            </label>
            <div className="relative">
              <input
                id="updateAt"
                type="date"
                className="w-full px-4 py-3 border-2 border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 bg-gray-800 text-white"
                {...register("updateAt", { required: true })}
              />
            </div>
          </div>
        </motion.div>

        {/* Action URL Field */}
        <motion.div variants={itemVariants}>
          <label
            htmlFor="actionUrl"
            className="block text-sm font-semibold text-gray-300 mb-2"
          >
            <div className="flex items-center gap-2">
              <Link className="w-4 h-4" />
              Action URL
            </div>
          </label>
          <div className="relative">
            <input
              id="actionUrl"
              type="url"
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 bg-gray-800 text-white placeholder-gray-500 ${
                errors.actionUrl
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500/50"
                  : "border-gray-700 focus:border-blue-500 focus:ring-blue-500/50"
              }`}
              placeholder="https://example.com"
              {...register("actionUrl", {
                pattern: {
                  value: /^https?:\/\/.+\..+/,
                  message: "Please enter a valid URL",
                },
              })}
            />
            <Link className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
          {errors.actionUrl && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-2 text-sm text-red-400 flex items-center gap-1"
            >
              <AlertCircle className="w-4 h-4" />
              {errors.actionUrl.message}
            </motion.p>
          )}
        </motion.div>

        {/* Is Active Field */}
        <motion.div variants={itemVariants}>
          <label className="flex items-center gap-3 p-4 border-2 border-gray-700 rounded-xl hover:border-gray-600 transition-all duration-200 cursor-pointer bg-gray-800">
            <input
              type="checkbox"
              className="hidden"
              {...register("isActive")}
            />
            <div
              className={`w-6 h-6 border-2 rounded-lg transition-all duration-200 flex items-center justify-center ${
                watch("isActive")
                  ? "border-green-500 bg-green-500"
                  : "border-gray-500"
              }`}
            >
              {watch("isActive") && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                  <CheckCircle className="w-4 h-4 text-white" />
                </motion.div>
              )}
            </div>
            <div>
              <span className="font-semibold text-gray-300">Active Update</span>
              <p className="text-sm text-gray-400">
                This update will be visible to students
              </p>
            </div>
          </label>
        </motion.div>

        {/* Submit Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-700"
          variants={itemVariants}
        >
          <motion.button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-br from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
            whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
          >
            {isSubmitting ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <RefreshCw className="w-5 h-5" />
                </motion.div>
                Creating Update...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Create Daily Update
              </>
            )}
          </motion.button>

          <motion.button
            type="button"
            onClick={() => reset()}
            className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-gray-600 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:bg-gray-800 transition-all duration-200"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw className="w-5 h-5" />
            Reset Form
          </motion.button>
        </motion.div>
      </form>
    </motion.div>
  );
};

export default AddDailyUpdate;
